import {
  BackupHealthStatus,
  FailbackStatus,
  RecoveryEnvironmentStatus,
  STATUS_TEXT,
  computeRecoveryEnvStatus,
} from 'src/app/account/components/application/statusText'
import { AppStatusState, ProcessStatus } from 'src/app/account/components/process-state/process-state.component'
import { SyncPhaseDto } from 'src/app/services/dtos'

// Tab-status-bar variants grouped by tab then sync phase, covering every state
// computeBackupHealthStatus / computeRecoveryEnvStatus / computeFailbackStatus can
// produce. Copy comes from STATUS_TEXT so wording stays in sync.
export const TAB_STATUS_GROUPS: ReadonlyArray<{
  key: string
  tab: string
  phases: ReadonlyArray<{
    phase: string
    variants: ReadonlyArray<{
      label: string
      status: BackupHealthStatus | RecoveryEnvironmentStatus | FailbackStatus
    }>
  }>
}> = [
  {
    key: 'tabStatusBackup',
    tab: 'Backup Health',
    phases: [
      {
        phase: 'Active backup (any sync phase)',
        variants: [
          {
            label: 'Creating initial backup — no prior success',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.backup.creatingInitial,
              progressCount: 3,
              progressTotal: 12,
              issues: [],
            },
          },
          {
            label: 'Creating backup — with prior success',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.backup.creatingNew,
              progressCount: 7,
              progressTotal: 12,
              priorBackupInfo: STATUS_TEXT.backup.priorBackupInfo(
                '2 hours ago',
                ' from recovery point at 01:14 PM on May 8',
                12
              ),
              issues: [],
            },
          },
        ],
      },
      {
        phase: 'Standby',
        variants: [
          {
            label: 'Planning backup — queued resources',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.backup.planningBackup,
              priorBackupInfo: STATUS_TEXT.backup.lastSuccessful(
                '4 hours ago',
                ' from recovery point at 11:14 AM on May 8'
              ),
              issues: [],
            },
          },
          {
            label: 'Last successful backup — clean',
            status: {
              state: AppStatusState.SUCCESS,
              text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', ' from recovery point at 01:14 PM on May 8.'),
              issues: [],
            },
          },
          {
            label: 'Last successful backup — with open issues',
            status: {
              state: AppStatusState.WARNING,
              text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', ' from recovery point at 01:14 PM on May 8.'),
              issues: [{} as any],
            },
          },
          {
            label: 'Last successful backup — partial (some resources didn’t process)',
            status: {
              state: AppStatusState.WARNING,
              text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', ' from recovery point at 01:14 PM on May 8.'),
              issues: [],
            },
          },
          {
            label: 'Failed to back up N resources',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.backup.failedToBackupCount(3, ' at 10:25 AM on May 9'),
              failedLink: { ...STATUS_TEXT.backup.failedLinkBackup, count: 3, suffix: ' at 10:25 AM on May 9.' },
              priorBackupInfo: STATUS_TEXT.backup.lastSuccessful(
                '1 day ago',
                ' from recovery point at 10:14 AM on May 8'
              ),
              issues: [],
            },
          },
          {
            label: 'Failed to create backup — no resource-level failures',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.backup.failedToCapture(' at 10:25 AM on May 9'),
              priorBackupInfo: STATUS_TEXT.backup.lastSuccessful(
                '1 day ago',
                ' from recovery point at 10:14 AM on May 8'
              ),
              issues: [],
            },
          },
        ],
      },
      {
        phase: 'Frozen (during recovery / test)',
        variants: [
          {
            label: 'Last successful backup — frozen during recovery',
            status: {
              state: AppStatusState.SUCCESS,
              text: STATUS_TEXT.backup.lastSuccessful('3 hours ago', ' from recovery point at 12:14 PM on May 8.'),
              issues: [],
            },
          },
          {
            label: 'No backup data available — no recovery point yet',
            status: { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.backup.noDataAvailable, issues: [] },
          },
        ],
      },
    ],
  },
  {
    key: 'tabStatusRecoveryEnv',
    tab: 'Recovery Environment',
    phases: [
      {
        phase: 'Standby',
        variants: [
          {
            label: 'Initial backup in progress',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.firstBackupInProgress,
              issues: [],
            },
          },
          {
            label: 'Waiting for first backup',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.waitingForFirstBackup,
              issues: [],
            },
          },
          {
            label: 'Waiting to perform initial restore',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.waitingForInitialRestore,
              issues: [],
            },
          },
          {
            label: 'Standby restore blocked by sibling failover',
            status: {
              state: AppStatusState.INFO,
              text: STATUS_TEXT.recovery.standbyRestoreBlocked('us-west-1', 'us-west-2'),
              issues: [],
            },
          },
          {
            label: 'Performing initial restore',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.performingInitialRestore,
              issues: [],
            },
          },
          {
            label: 'Restoring backup with progress',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
              ratioText: '40% restored (4/10) from recovery point at 03:14 PM on May 8.',
              progressCount: 4,
              progressTotal: 10,
              issues: [],
            },
          },
          // Run through the real computeRecoveryEnvStatus so these exercise the resume warming-up
          // gate (no bar/ratio until a resource completes), not a hand-authored look-alike.
          {
            label: 'Resume — warming up (0 of 8 restored, bar suppressed)',
            status: computeRecoveryEnvStatus({
              syncPhase: SyncPhaseDto.FAILOVER,
              syncStatus: ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS,
              appliedRpTimestamp: new Date(),
              failedRestoreCount: 0,
              restoreIssues: [],
              appIsDeleted: false,
              allResourcesCleaned: false,
              successCount: 0,
              totalCount: 8,
              inProgressCount: 8,
            }),
          },
          {
            label: 'Resume — first resource done (1 of 8, bar returns)',
            status: computeRecoveryEnvStatus({
              syncPhase: SyncPhaseDto.FAILOVER,
              syncStatus: ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS,
              appliedRpTimestamp: new Date(),
              failedRestoreCount: 0,
              restoreIssues: [],
              appIsDeleted: false,
              allResourcesCleaned: false,
              successCount: 1,
              totalCount: 8,
              inProgressCount: 7,
            }),
          },
          {
            // A fresh failure (not just a success) also ends the warming-up window, so the bar
            // returns at an accurate 0% rather than staying an indeterminate spinner.
            label: 'Resume — first resource failed (1 of 8 failed, bar returns at 0%)',
            status: computeRecoveryEnvStatus({
              syncPhase: SyncPhaseDto.FAILOVER,
              syncStatus: ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS,
              appliedRpTimestamp: new Date(),
              failedRestoreCount: 1,
              restoreIssues: [],
              appIsDeleted: false,
              allResourcesCleaned: false,
              successCount: 0,
              totalCount: 8,
              inProgressCount: 7,
            }),
          },
          {
            label: 'Successfully restored',
            status: {
              state: AppStatusState.SUCCESS,
              text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
              ratioText: '100% restored (8/8) from recovery point at 03:14 PM on May 8.',
              issues: [],
            },
          },
          {
            label: 'Successfully restored with open issues',
            status: {
              state: AppStatusState.WARNING,
              text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
              ratioText: '100% restored (8/8) from recovery point at 03:14 PM on May 8.',
              issues: [{} as any, {} as any],
            },
          },
          {
            label: 'Failed to restore N resources',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.recovery.failedToRestoreCount(2, '1 hour ago'),
              ratioText: '75% restored (6/8) from recovery point at 03:14 PM on May 8.',
              failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('1 hour ago'), count: 2 },
              issues: [],
            },
          },
          {
            label: 'Initial restore failed',
            status: { state: AppStatusState.ERROR, text: STATUS_TEXT.recovery.initialRestoreFailed, issues: [] },
          },
          {
            label: 'Failed to apply backup',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.recovery.failedToApplyBackup('30 minutes ago'),
              issues: [],
            },
          },
        ],
      },
      {
        phase: 'Failover',
        variants: [
          {
            label: 'Preparing to restore for recovery',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.preparingRestore(false),
              issues: [],
            },
          },
          {
            label: 'Restoring for recovery with progress',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.restoreInProgress(false),
              ratioText: '60% restored (6/10) from recovery point at 03:14 PM on May 8.',
              progressCount: 6,
              progressTotal: 10,
              issues: [],
            },
          },
          {
            label: 'All resources restored for recovery',
            status: {
              state: AppStatusState.SUCCESS,
              text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', false),
              ratioText: '100% restored (10/10) from recovery point at 03:14 PM on May 8.',
              issues: [],
            },
          },
          {
            label: 'Failed to restore N resources for recovery',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.recovery.failedToRestoreCount(3, '15 minutes ago'),
              ratioText: '70% restored (7/10) from recovery point at 03:14 PM on May 8.',
              failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('15 minutes ago'), count: 3 },
              issues: [],
            },
          },
          {
            label: 'Restore for recovery failed — terminal',
            status: { state: AppStatusState.ERROR, text: STATUS_TEXT.recovery.restoreFailed(false), issues: [] },
          },
        ],
      },
      {
        phase: 'Failover Test',
        variants: [
          {
            label: 'Preparing to restore for test recovery',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.preparingRestore(true),
              issues: [],
            },
          },
          {
            label: 'Restoring for test recovery with progress',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.restoreInProgress(true),
              ratioText: '60% restored (6/10) from recovery point at 03:14 PM on May 8.',
              progressCount: 6,
              progressTotal: 10,
              issues: [],
            },
          },
          {
            label: 'All resources restored for test recovery',
            status: {
              state: AppStatusState.SUCCESS,
              text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', true),
              ratioText: '100% restored (10/10) from recovery point at 03:14 PM on May 8.',
              issues: [],
            },
          },
          {
            label: 'Failed to restore N resources for test recovery',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.recovery.failedToRestoreCount(3, '15 minutes ago'),
              ratioText: '70% restored (7/10) from recovery point at 03:14 PM on May 8.',
              failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('15 minutes ago'), count: 3 },
              issues: [],
            },
          },
          {
            label: 'Restore for test recovery failed — terminal',
            status: { state: AppStatusState.ERROR, text: STATUS_TEXT.recovery.restoreFailed(true), issues: [] },
          },
        ],
      },
      {
        phase: 'Deleted app (cleanup)',
        variants: [
          {
            label: 'Cleaning up recovery environment resources',
            status: { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.recovery.cleaningUp, issues: [] },
          },
          {
            label: 'Finalizing application deletion',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.recovery.finalizingDeletion,
              issues: [],
            },
          },
          {
            label: 'Recovery environment cleanup failed',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.recovery.cleanupFailed,
              issues: [{} as any],
            },
          },
        ],
      },
    ],
  },
  {
    key: 'tabStatusFailback',
    tab: 'Failback',
    phases: [
      {
        phase: 'Any sync phase',
        variants: [
          {
            label: 'No resources support failback',
            status: { state: AppStatusState.INFO, text: STATUS_TEXT.failback.noSupported, issues: [] },
          },
          {
            label: 'Resources available for failback',
            status: { state: AppStatusState.SUCCESS, text: STATUS_TEXT.failback.available(8), issues: [] },
          },
          {
            label: 'Returning resources with progress',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.failback.returning,
              ratioText: '50% returned (4/8).',
              progressCount: 4,
              progressTotal: 8,
              issues: [],
            },
          },
          {
            label: 'Retrying return to primary',
            status: {
              state: AppStatusState.INDETERMINATE,
              text: STATUS_TEXT.failback.retrying,
              ratioText: '75% returned (6/8).',
              progressCount: 6,
              progressTotal: 8,
              issues: [],
            },
          },
          {
            label: 'All resources returned',
            status: {
              state: AppStatusState.SUCCESS,
              text: STATUS_TEXT.failback.allReturned,
              ratioText: '100% returned (8/8).',
              issues: [],
            },
          },
          {
            label: 'Failed to return N resources',
            status: {
              state: AppStatusState.ERROR,
              text: STATUS_TEXT.failback.failedToReturnCount(2),
              ratioText: '75% returned (6/8).',
              failedLink: { ...STATUS_TEXT.failback.failedLinkReturn, count: 2 },
              issues: [],
            },
          },
          {
            label: 'Failback failed — terminal',
            status: { state: AppStatusState.ERROR, text: STATUS_TEXT.failback.failed, issues: [] },
          },
        ],
      },
    ],
  },
]
